import React, { useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { MENU_ROLES } from "../mock/mock";
import WIBClock from "../components/WIBClock";
import {
  LayoutDashboard, Megaphone, Wallet, Users, AlertTriangle, CalendarDays, Image, FileText, Store,
  PhoneCall, Settings, UserCog, LogOut, ChevronLeft, Menu, PiggyBank, Bell, ShieldCheck, Leaf, Share2, Archive
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Avatar, AvatarFallback } from "../components/ui/avatar";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "../components/ui/dropdown-menu";

const MENU = [
  { key: "dashboard", to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
  { key: "pengumuman", to: "/pengumuman", label: "Pengumuman", icon: Megaphone },
  { key: "iuran", to: "/iuran", label: "Iuran Warga", icon: Wallet },
  { key: "kas", to: "/kas", label: "Kas RT", icon: PiggyBank },
  { key: "data-warga", to: "/data-warga", label: "Data Warga", icon: Users },
  { key: "laporan", to: "/laporan", label: "Laporan Kejadian", icon: AlertTriangle },
  { key: "kegiatan", to: "/kegiatan", label: "Kegiatan", icon: CalendarDays },
  { key: "jadwal-satpam", to: "/jadwal-satpam", label: "Jadwal Satpam", icon: ShieldCheck },
  { key: "identitas-satpam", to: "/identitas-satpam", label: "Identitas Satpam", icon: ShieldCheck, adminOnly: true },
  { key: "layanan-lingkungan", to: "/layanan-lingkungan", label: "Petugas Lingkungan", icon: Leaf },
  { key: "arsip-laporan", to: "/arsip-laporan", label: "Arsip Laporan", icon: Archive },
  { key: "galeri", to: "/galeri", label: "Galeri", icon: Image },
  { key: "surat", to: "/surat", label: "Surat Menyurat", icon: FileText },
  { key: "umkm", to: "/umkm", label: "UMKM Warga", icon: Store },
  { key: "kontak-darurat", to: "/kontak-darurat", label: "Kontak Darurat", icon: PhoneCall },
  { key: "bagikan-undangan", to: "/bagikan-undangan", label: "Bagikan Undangan", icon: Share2 },
  { key: "user-management", to: "/user-management", label: "Kelola Pengguna", icon: UserCog, adminOnly: true },
  { key: "pengaturan", to: "/pengaturan", label: "Pengaturan", icon: Settings, adminOnly: true },
];

const DashboardLayout = () => {
  const { user, logout, theme } = useApp();
  const nav = useNavigate();
  const [open, setOpen] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);

  const allowed = MENU_ROLES[user.role] || [];
  const items = MENU.filter((m) => allowed.includes(m.key));

  const initials = user.name.split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
  const roleLabel = user.role === "super_admin" ? "Super Admin" : user.role === "admin" ? "Admin RT" : "Warga";

  const doLogout = () => { logout(); nav("/login"); };

  return (
    <div className="min-h-screen flex bg-slate-50">
      {/* Sidebar Desktop */}
      <aside className={`hidden lg:flex flex-col bg-white border-r border-slate-200 transition-all ${open ? "w-64" : "w-20"}`}>
        <div className="h-16 flex items-center gap-3 px-4 border-b border-slate-100">
          <img src={theme.logo} alt="logo" className="h-10 w-10 rounded-full object-cover shadow-sm" />
          {open && (
            <div className="leading-tight">
              <div className="font-bold text-emerald-800 text-sm">{theme.appName.split(" ").slice(0, 2).join(" ")}</div>
              <div className="text-[10px] text-amber-600 font-semibold tracking-widest">{theme.appTagline}</div>
            </div>
          )}
        </div>
        <nav className="flex-1 overflow-y-auto scrollbar-thin py-3 px-2 space-y-0.5">
          {items.map((m) => (
            <NavLink key={m.key} to={m.to} end={m.end} className={({ isActive }) => `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition group ${isActive ? "brand-gradient text-white shadow-md" : "text-slate-600 hover:bg-emerald-50 hover:text-emerald-800"}`}>
              <m.icon className="h-4.5 w-4.5 shrink-0" style={{ width: 18, height: 18 }} />
              {open && <span className="truncate">{m.label}</span>}
              {open && m.adminOnly && <span className="ml-auto text-[9px] rounded bg-amber-400 text-white px-1.5 py-0.5 font-bold">ADMIN</span>}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-slate-100">
          <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-center gap-2 text-xs text-slate-500 hover:text-emerald-700 py-2">
            <ChevronLeft className={`h-4 w-4 transition ${!open && "rotate-180"}`} /> {open && "Sembunyikan"}
          </button>
        </div>
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-slate-900/60" onClick={() => setMobileOpen(false)} />
          <aside className="absolute left-0 top-0 bottom-0 w-72 bg-white shadow-xl flex flex-col">
            <div className="h-16 flex items-center gap-3 px-4 border-b">
              <img src={theme.logo} alt="logo" className="h-10 w-10 rounded-full object-cover" />
              <div className="font-bold text-emerald-800 text-sm">{theme.appName}</div>
            </div>
            <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-0.5">
              {items.map((m) => (
                <NavLink key={m.key} to={m.to} end={m.end} onClick={() => setMobileOpen(false)} className={({ isActive }) => `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium ${isActive ? "brand-gradient text-white" : "text-slate-600 hover:bg-emerald-50"}`}>
                  <m.icon style={{ width: 18, height: 18 }} />
                  <span>{m.label}</span>
                </NavLink>
              ))}
            </nav>
          </aside>
        </div>
      )}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 bg-white border-b border-slate-200 flex items-center px-4 lg:px-6 gap-4 sticky top-0 z-30">
          <button className="lg:hidden p-2 rounded hover:bg-slate-100" onClick={() => setMobileOpen(true)}><Menu className="h-5 w-5" /></button>
          <div className="flex-1 min-w-0">
            <div className="font-bold text-slate-800 truncate text-sm md:text-base" style={{ fontFamily: "Poppins, sans-serif" }}>{theme.headerTitle}</div>
            <div className="text-[10px] md:text-xs text-amber-600 font-semibold tracking-widest">{theme.appTagline}</div>
          </div>
          <WIBClock compact className="hidden md:flex" />
          <button className="relative p-2 rounded-full hover:bg-slate-100">
            <Bell className="h-5 w-5 text-slate-600" />
            <span className="absolute top-1.5 right-1.5 h-2 w-2 rounded-full bg-amber-500" />
          </button>
          <DropdownMenu>
            <DropdownMenuTrigger className="outline-none">
              <div className="flex items-center gap-2 rounded-full pl-1 pr-3 py-1 hover:bg-slate-100 transition">
                <Avatar className="h-8 w-8"><AvatarFallback className="brand-gradient text-white text-xs font-bold">{initials}</AvatarFallback></Avatar>
                <div className="text-left hidden sm:block">
                  <div className="text-xs font-semibold text-slate-800 truncate max-w-[120px]">{user.name}</div>
                  <div className="text-[10px] text-emerald-700 font-medium">{roleLabel}</div>
                </div>
              </div>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <div className="font-semibold">{user.name}</div>
                <div className="text-xs text-slate-500 font-normal">{user.email}</div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={doLogout} className="text-red-600"><LogOut className="h-4 w-4 mr-2" /> Keluar</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>
        <main className="flex-1 p-4 md:p-6 lg:p-8 max-w-[1400px] w-full mx-auto">
          <Outlet />
        </main>
        <footer className="py-4 text-center text-xs text-slate-500">{theme.footerText || `© ${new Date().getFullYear()} ${theme.appName} — ${theme.appTagline}`}</footer>
      </div>
    </div>
  );
};

export default DashboardLayout;
